// stores/useScehduleStore.ts

import { computed, ref } from 'vue';

import type { AppProps, ScheduleType } from '@/types/appTypes';

// Our Schedule helper functions. , getActiveBASE, getActiveOVERRIDE
import { getActiveBASE, getActiveOVERRIDE, getActiveSchedule } from '@/composables/useActiveSchedule';

export const useScheduleStore = () => {
    const dateRangeBases = ref<ScheduleType[]>([]);
    const dateRangeOverrides = ref<ScheduleType[]>([]);

    // const sortedOverridesList = computed(() => [...dateRangeOverrides.value].sort((a, b) => b.id - a.id));

    /**
     * Get the active schedule for a given date string (YYYY-MM-DD)
     * considering both base and override schedules.
     * @returns ScheduleType | null
     */
    const getActiveScheduleForDate = (dateString: string): ScheduleType | null => {
        return getActiveSchedule(dateString, dateRangeBases.value, dateRangeOverrides.value);
    };

    const _getActiveOverrideForDate = (dateString: string): ScheduleType | null => {
        return getActiveOVERRIDE(dateString, dateRangeOverrides.value);
    };

    const getActiveBASEForDate = (dateString: string): ScheduleType | null => {
        return getActiveBASE(dateString, dateRangeBases.value);
    };

    /**
     * Return the TimeHint string for a given date and slot index.
     * Uses the Active Schedule.
     * @returns string | null
     */
    function getTimeHintForDateSlot(dateString: string, slotIndex: number): string | null {
        const activeSchedule = getActiveScheduleForDate(dateString);
        if (!activeSchedule) {
            return null;
        }

        const slot = activeSchedule.slots?.[slotIndex-1];
        return slot ? slot.timehint : null;
    }

    /**
     * Using this to display max slots in WeekCal
     * Is a bit tricky, as we need to consider both base and override schedules.
     * At the moment I'm showing the greater of the two max slot counts. This gives
     * disabled slots in case of an override with fewer slots than the base.
     *
     * Get maximum slots count from all schedules in the date range
     * (both base and override schedules)
     * @returns number
     */
    const dateRangeMaxSlots = computed(() => {
        let maxBaseSlotCount = 0;
        let maxOverSlotCount = 0;

        // If an Override is found, use its Slots count.
        if (dateRangeOverrides.value.length > 0) {
            const overrides = dateRangeOverrides.value || [];
            if (Array.isArray(overrides)) {
                overrides.forEach((schedule) => {
                    maxOverSlotCount = Math.max(maxOverSlotCount, schedule.slots?.length || 0);
                });
            }
        }

        // Otherwise, check Base schedules for their Slots count.
        const bases = dateRangeBases.value || [];
        if (Array.isArray(bases)) {
            bases.forEach((schedule) => {
                maxBaseSlotCount = Math.max(maxBaseSlotCount, schedule.slots?.length || 0);
            });
        }

        // Return the higher of the two max counts
        return Math.max(maxBaseSlotCount, maxOverSlotCount);
    });

    /**
     * This gets called from initStores syncStoresFromProps()
     * In theory it should always get the latest data required for this store
     * whenever the usePage().props changes on navigation.
     * @param props
     */
    const _updateFromProps = (props: AppProps) => {
        //currentSchedule.value = props.currentSchedule;
        dateRangeBases.value = Array.isArray(props.activeSchedules_base) ? props.activeSchedules_base : [];
        dateRangeOverrides.value = Array.isArray(props.activeSchedules_overrides) ? props.activeSchedules_overrides : [];
        //console.log('Schedule Store updated from props.');
    };

    return {
        dateRangeBases,
        dateRangeOverrides,
        //
        dateRangeMaxSlots,
        getActiveScheduleForDate,
        getActiveOverrideForDate: _getActiveOverrideForDate,
        getActiveBASEForDate,
        //
        getTimeHintForDateSlot,
        // TODO: When rebuilding Schedule management UI, add
        // CRUD as well here.

        // Used to sync store from Inertia props on page load/navigation
        updateFromProps: _updateFromProps,
    };

    /**
     *
     * ================================================
     * Helper Functions
     * ================================================
     *
     */
};
